import type { AthenaPostgresPool } from "../../../../postgres/driver.ts";
import type { AthenaClientInternals } from "../../../../runtime/client-internals.ts";
import {
	ATHENA_EVENT_INGRESS_PERSISTENCE_REQUIRED,
	ATHENA_INGRESS_MOLLIE_UNAVAILABLE,
	AthenaEventIngressError,
} from "../../../../runtime/ingress/errors.ts";
import type { AthenaIngressIR } from "../../../../runtime/ingress/ir.ts";
import { eventIngressDatabaseFromPool } from "../../../../runtime/ingress/postgres.ts";
import { createEventIngressRuntime } from "../../../../runtime/ingress/runtime.ts";
import { createSqlEventIngressPersistence } from "../../../../runtime/ingress/sql.ts";
import { runBillingCustomerImportWithPostgres } from "../../../import/assemble.ts";
import { createBillingImportDatabase } from "../../../import/database.ts";
import {
	createBillingSqlExecutor,
	listActiveBillingImportConnections,
	resolveBillingImportConnection,
} from "../../../import/postgres.ts";
import { createLazyBillingWebhookIngressObservability } from "../../../ingestion/observability/health.ts";
import { createPostgresBillingWebhookRegistrationStore } from "../../../ingestion/reconciliation/repository.ts";
import type { BillingProviderConfigMap } from "../../../providers/types.ts";
import { billingReconciliationContextFromIngress } from "../../../reconciliation/webhook.ts";
import type { BillingSqlExecutor } from "../../../subject/repository.ts";
import {
	createConnectionProviderExecutionContext,
	mollieConfigForCredentialReference,
} from "../providers/connection-binding.ts";
import { normalizeMollieRuntimeConfig } from "../providers/mollie/config.ts";
import { resolveMollieWebhookResource } from "../providers/mollie/resolve-resource.ts";
import { MollieSdkClientPool } from "../providers/mollie/sdk/client-factory.ts";
import { createBillingProviderRegistry } from "../providers/create-registry.ts";
import { createMollieWebhookPort } from "../providers/mollie/webhook-port.ts";
import { advanceSelfSubscriptionEnrollment } from "../../self/advance-enrollment.ts";
import { createBillingEventIngressHandler } from "./handler.ts";
import { createSqlBillingRepositories } from "./sql.ts";

function isAthenaPostgresPool(value: unknown): value is AthenaPostgresPool {
	return (
		value != null &&
		typeof value === "object" &&
		typeof (value as AthenaPostgresPool).query === "function" &&
		typeof (value as AthenaPostgresPool).connect === "function"
	);
}

export function attachEventIngressRuntime(
	internals: AthenaClientInternals,
	input: {
		configuredProviders?: BillingProviderConfigMap;
		testMode?: boolean;
		signingSecrets?: readonly string[];
	},
): void {
	const getSql = async (): Promise<BillingSqlExecutor | undefined> => {
		const owned = internals.postgresRuntime;
		const pool = owned ? await owned.getPool() : undefined;
		if (!isAthenaPostgresPool(pool)) {
			return undefined;
		}
		return createBillingSqlExecutor(pool);
	};
	const handler = createBillingEventIngressHandler({
		observability: createLazyBillingWebhookIngressObservability(getSql),
		onAuthoritativeDocument: async ({ document, ingress }) => {
			const owned = internals.postgresRuntime;
			const pool = owned ? await owned.getPool() : undefined;
			if (!isAthenaPostgresPool(pool)) {
				return;
			}
			const sql = createBillingSqlExecutor(pool);
			if (document.kind === "payment") {
				try {
					const registry = createBillingProviderRegistry(
						input.configuredProviders,
					);
					await advanceSelfSubscriptionEnrollment({
						configuredProviders: input.configuredProviders,
						document,
						registry,
						sql,
						testMode: input.testMode,
					});
				} catch (error) {
					console.error("[athena-billing] enrollment advance failed", {
						error: error instanceof Error ? error.message : String(error),
						ingressId: ingress.id,
					});
					throw error;
				}
			}
			const providerCustomerId = document.providerCustomerId;
			const connectionId = ingress.connectionId;
			if (
				typeof providerCustomerId !== "string" ||
				providerCustomerId.length === 0 ||
				typeof connectionId !== "string" ||
				connectionId.length === 0
			) {
				return;
			}
			try {
				if (typeof connectionId === "string" && connectionId.length > 0) {
					const kind =
						ingress.operation === "webhook.mollie.classic"
							? "classic"
							: "next_gen";
					await createPostgresBillingWebhookRegistrationStore(
						sql,
					).touchLastDelivery({
						connectionId,
						kind,
					});
				}
				await runBillingCustomerImportWithPostgres({
					configuredProviders: input.configuredProviders,
					connectionId,
					customerId: providerCustomerId,
					database: createBillingImportDatabase(pool),
					dryRun: false,
					pool,
					sql,
					testMode: input.testMode,
					trigger: "webhook",
					reconciliationContext: billingReconciliationContextFromIngress({
						connectionId,
						correlationId: ingress.correlationId,
						ingressId: ingress.id,
						provider: document.provider,
						traceId: ingress.traceId,
					}),
				});
			} catch (error) {
				console.error("[athena-billing] webhook reconciliation failed", {
					connectionId,
					error: error instanceof Error ? error.message : String(error),
					ingressId: ingress.id,
				});
				throw error;
			}
		},
		port: createMollieWebhookPort({
			resolveResource: createMollieResourceResolver({
				configuredProviders: input.configuredProviders,
				getSql,
			}),
			signingSecrets: input.signingSecrets,
			verification:
				input.signingSecrets && input.signingSecrets.length > 0
					? "signature_and_refetch"
					: "authoritative_refetch",
		}),
	});

	internals.eventIngressRuntime = {
		async ingest(ingress: AthenaIngressIR) {
			const owned = internals.postgresRuntime;
			const pool = owned ? await owned.getPool() : undefined;
			if (!isAthenaPostgresPool(pool)) {
				throw new AthenaEventIngressError({
					code: ATHENA_EVENT_INGRESS_PERSISTENCE_REQUIRED,
					domain: "billing",
					message:
						"Financial webhook ingest requires durable PostgreSQL persistence.",
					retryable: true,
				});
			}
			const sqlRepos = createSqlBillingRepositories();
			const runtime = createEventIngressRuntime({
				context: {
					database: eventIngressDatabaseFromPool(pool),
					persistence: createSqlEventIngressPersistence(),
					domain: sqlRepos,
				},
				handler,
			});
			return runtime.ingest(ingress);
		},
	};
}

function unavailableMollieRefetch(message: string): never {
	throw new AthenaEventIngressError({
		code: ATHENA_INGRESS_MOLLIE_UNAVAILABLE,
		domain: "billing",
		message,
		provider: "mollie",
		retryable: false,
	});
}

function createMollieResourceResolver(input: {
	configuredProviders?: BillingProviderConfigMap;
	getSql: () => Promise<BillingSqlExecutor | undefined>;
}): (request: {
	connectionId?: string;
	id: string;
	kind: "payment" | "subscription" | "invoice";
}) => Promise<unknown> {
	const pools = new Map<string, MollieSdkClientPool>();
	return async (request) => {
		const sql = await input.getSql();
		if (sql == null) {
			unavailableMollieRefetch(
				"Mollie webhook resource refetch requires PostgreSQL so the connection credential can be resolved.",
			);
		}
		const connection =
			request.connectionId != null && request.connectionId.length > 0
				? await resolveBillingImportConnection(sql, {
					connectionId: request.connectionId,
					provider: "mollie",
				})
				: await resolveSoleActiveMollieConnection(sql);
		const mollie = mollieConfigForCredentialReference(
			input.configuredProviders,
			connection.credentialReference,
		);
		if (mollie == null) {
			unavailableMollieRefetch(
				`Mollie is not configured for credential_reference "${connection.credentialReference}".`,
			);
		}
		let pool = pools.get(connection.credentialReference);
		if (pool == null) {
			pool = new MollieSdkClientPool(normalizeMollieRuntimeConfig(mollie));
			pools.set(connection.credentialReference, pool);
		}
		const context = createConnectionProviderExecutionContext({
			configuredProviders: input.configuredProviders,
			connection,
			operationScope:
				request.kind === "invoice" ? "organization" : "profile",
		});
		return resolveMollieWebhookResource({
			credential: context.credential,
			id: request.id,
			kind: request.kind,
			operationScope: context.operationScope,
			pool,
			profileId: context.target.profileId,
		});
	};
}

async function resolveSoleActiveMollieConnection(sql: BillingSqlExecutor) {
	const active = await listActiveBillingImportConnections(sql, "mollie");
	const only = active[0];
	if (only == null) {
		unavailableMollieRefetch(
			"No active Mollie billing connection is available for webhook resource refetch.",
		);
	}
	if (active.length > 1) {
		unavailableMollieRefetch(
			"Multiple active Mollie billing connections: webhook refetch requires ingress.connectionId.",
		);
	}
	return only;
}
